/**
 * KitchenDisplayPage.jsx — fullscreen Kitchen Display Screen (KDS).
 *
 * Route: /venue-terminal/:vendorId/kitchen
 *
 * Chef-tappable line-by-line status flow:
 *   queued  -> cooking -> plating -> ready -> served
 * Colour codes for age:
 *   green (<8min) · amber (<15min) · red pulsing (>15min)
 * Cooking transition uses each item's prep_time_minutes to render an
 * "N min to plate" countdown.
 * Kiosk-locked: needs a PIN to view; auto-refreshes every 6 seconds.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ChefHat, Users, Maximize, RefreshCw, LogOut, User, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useKiosk, kioskAuthHeader } from "@/lib/kioskAuth";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const ageColour = (sentAt) => {
  if (!sentAt) return "border-stone-700";
  const mins = (Date.now() - new Date(sentAt).getTime()) / 60000;
  if (mins < 8)  return "border-emerald-400 shadow-emerald-500/30";
  if (mins < 15) return "border-amber-400 shadow-amber-500/30";
  return "border-red-500 shadow-red-500/30 animate-pulse";
};
const relTime = (sentAt) => {
  if (!sentAt) return "";
  const s = Math.max(0, (Date.now() - new Date(sentAt).getTime()) / 1000);
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return m ? `${m}m ${r}s` : `${r}s`;
};
const etaText = (iso) => {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "due now";
  return `${Math.ceil(ms / 60000)}m to plate`;
};

const NEXT_PHASE  = { queued: "cooking", cooking: "plating",
                      plating: "ready", ready: "served" };
const PHASE_LABEL = {
  queued:  { text: "Start cooking", bg: "bg-amber-500 text-slate-950" },
  cooking: { text: "Plating",       bg: "bg-sky-500 text-white" },
  plating: { text: "Ready",         bg: "bg-emerald-500 text-slate-950" },
  ready:   { text: "Served",        bg: "bg-stone-500 text-white" },
};
const PHASE_CHIP = {
  queued:  "bg-stone-700 text-stone-100",
  cooking: "bg-amber-500 text-slate-950",
  plating: "bg-sky-500 text-white",
  ready:   "bg-emerald-500 text-slate-950",
};

export default function KitchenDisplayPage() {
  const { vendorId }        = useParams();
  const { staff, signOut }  = useKiosk(vendorId);
  const [rows, setRows]     = useState([]);
  const [busy, setBusy]     = useState(false);
  const [tick, setTick]     = useState(0);
  const [err, setErr]       = useState("");
  const cfg = { headers: kioskAuthHeader(), withCredentials: true };

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/venue-terminal/${vendorId}/kds`, cfg);
      setRows(data.kds || []); setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Not authenticated");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    document.title = "Kitchen · HustleMeetups";
    load();
    const poll = setInterval(load, 6000);
    const tickInt = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(tickInt); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const goFull = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
  };

  const flipStatus = async (ticketId, lineId, current) => {
    const next = NEXT_PHASE[current || "queued"];
    if (!next) return;
    try {
      await axios.post(
        `${API}/venue-terminal/tickets/${ticketId}/items/${lineId}/status`,
        { status: next }, cfg);
      setRows((prev) => prev.map((r) => r.ticket_id === ticketId
        ? { ...r, items: r.items.map((it) => it.line_id === lineId
              ? { ...it, status: next,
                  expected_finish_at: (next === "cooking" && !it.expected_finish_at)
                    ? new Date(Date.now() + (it.prep_time_minutes || 12) * 60000).toISOString()
                    : it.expected_finish_at }
              : it) }
        : r));
      if (next === "served") load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status flip failed");
    }
  };

  return (
    <div
      data-testid="kitchen-display-page"
      className="min-h-screen bg-black text-stone-100"
    >
      <header className="sticky top-0 z-10 bg-black/85 backdrop-blur border-b border-stone-800 px-4 py-3 flex items-center gap-3 flex-wrap">
        <ChefHat size={22} className="text-amber-400"/>
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] text-amber-400 font-black">Kitchen Display</p>
          <h1 className="text-lg font-black">{vendorId}</h1>
        </div>
        {staff && (
          <span
            data-testid="kds-staff-badge"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-emerald-500/20 text-emerald-300 font-black px-2 py-1 rounded-full"
          >
            <User size={11}/> {staff.name} · {staff.role}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <span
            data-testid="kds-live-count"
            className="text-[10px] uppercase tracking-widest font-black bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-full tabular-nums"
          >
            {rows.length} open · {tick % 60}s
          </span>
          <Button onClick={load} variant="outline"
                  className="rounded-full font-black bg-transparent text-white border-stone-700"
                  data-testid="btn-kds-refresh">
            <RefreshCw size={14} className="mr-1"/> Refresh
          </Button>
          <Button onClick={goFull}
                  data-testid="btn-fullscreen"
                  className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black">
            <Maximize size={14} className="mr-1"/> Fullscreen
          </Button>
          <Button onClick={signOut}
                  data-testid="btn-kds-signout"
                  variant="outline"
                  className="rounded-full font-black bg-transparent text-white border-stone-700">
            <LogOut size={14}/>
          </Button>
        </div>
      </header>

      <main className="p-4">
        {err && (
          <div data-testid="kds-err"
               className="rounded-xl bg-red-950/50 border border-red-800 p-3 text-sm text-red-200">
            {err}
          </div>
        )}
        {!err && rows.length === 0 && !busy && (
          <div data-testid="kds-empty"
               className="text-center py-24 text-stone-500">
            <ChefHat size={48} className="mx-auto mb-4 opacity-40"/>
            <p className="text-lg font-black">Nothing fired yet</p>
            <p className="mt-1 text-sm">Waiters will send tickets from the Terminal.</p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {rows.map((r) => (
            <div
              key={r.ticket_id}
              data-testid={`kds-card-${r.ticket_id}`}
              className={`rounded-2xl border-2 shadow-lg bg-stone-900 p-4 ${ageColour(r.sent_at)}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-3xl font-black text-amber-400">{r.table_no}</span>
                <div className="ml-auto text-right">
                  <p className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Fired</p>
                  <p className="text-sm font-black tabular-nums">{relTime(r.sent_at)}</p>
                </div>
              </div>
              <p className="text-[11px] text-stone-400 inline-flex items-center gap-1 mb-3">
                <Users size={11}/> {r.guests || "?"} guests · {r.server_name || "server"}
              </p>
              <ul className="space-y-2">
                {(r.items || []).map((it) => {
                  const phase = it.status || "queued";
                  const nxt   = NEXT_PHASE[phase];
                  return (
                    <li key={it.line_id}
                        data-testid={`kds-line-${it.line_id}`}
                        className="rounded-xl bg-stone-800/60 p-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-amber-400 tabular-nums text-lg font-black">{it.qty}×</span>
                        <span className="flex-1 text-lg font-black">{it.name}</span>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-black ${PHASE_CHIP[phase]}`}>
                          {phase}
                        </span>
                      </div>
                      {phase === "cooking" && it.expected_finish_at && (
                        <p className="mt-1 text-[11px] text-amber-300 font-black tabular-nums">
                          {etaText(it.expected_finish_at)}
                        </p>
                      )}
                      {nxt && (
                        <button
                          data-testid={`btn-flip-${it.line_id}`}
                          onClick={() => flipStatus(r.ticket_id, it.line_id, phase)}
                          className={`mt-2 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-black ${PHASE_LABEL[phase].bg}`}
                        >
                          <CheckCircle2 size={11}/> {PHASE_LABEL[phase].text}
                        </button>
                      )}
                    </li>
                  );
                })}
                {(r.items || []).length === 0 && (
                  <li className="text-xs text-stone-500 italic">nothing on ticket</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
